import { useMemo, useState } from "react";
import { useSCBOneOperationRunLimit } from "../../api/api.scb-one";
import { SCBOneDevice } from "../../api/models";
import { AboutCard } from "../cards/about/AboutCard";
import { SCBOneAlertCard } from "../cards/alert/AlertCard";
import { SCBOneLocationCard } from "../cards/location/LocationCard";
import { SCBOneMapCard } from "../cards/map/MapCard";
import { SCBOneOperationRunsCard } from "../cards/operation-run/OperationRunsCard";
import { SCBOneOperationRunPositionCard } from "../cards/position/PositionCard";

export interface SelectedOperationRun {
  runId: string;
  serial: string | undefined;
  startDate: Date;
  endDate: Date | null;
}

interface SCBOneDetailsProps {
  device: SCBOneDevice;
  breadcrumb: React.ReactElement;
}

export function SCBOneDetails({ device, breadcrumb }: SCBOneDetailsProps) {
  const [selectedOperaionRun, setSelectedOperationRun] = useState<
    SelectedOperationRun | undefined
  >();

  /*
    For map and download operation run positions, we need to fetch all positions.
    Doing at top level to avoid multiple fetches.

    For positions card, need to fetch paginated positions, and a separate fetch
    is done in the card component.
  */
  const operationRunPositions = useSCBOneOperationRunLimit(
    selectedOperaionRun?.runId,
    device.serial
  );

  const memoizedOperationRunPositions = useMemo(() => {
    return operationRunPositions.value?.items || [];
  }, [operationRunPositions.value?.items]);

  const onRowSelected = (selecteRun: SelectedOperationRun) => {
    setSelectedOperationRun({
      runId: selecteRun.runId,
      serial: selecteRun.serial,
      startDate: selecteRun.startDate,
      endDate: selecteRun.endDate,
    });
  };

  const alertFromToFilter = useMemo(() => {

    return {
      from: selectedOperaionRun?.startDate
        ? new Date(selectedOperaionRun.startDate)
        : null,
      to: selectedOperaionRun?.endDate
        ? new Date(selectedOperaionRun.endDate)
        : null,
    };
  }, [selectedOperaionRun]);

  return (
    <div className="screen-with-breadcrumb">
      <div className="screen-with-filter-header">
        <div style={{ justifySelf: "start" }}>{breadcrumb}</div>
      </div>
      <div className="card-container">
        <AboutCard beacon={device} deviceType="scb-one" />
        <SCBOneMapCard
          positions={memoizedOperationRunPositions || []}
          isLoading={operationRunPositions.loading}
          operationRunSelected={!!selectedOperaionRun?.runId}
          selectedOperationRun={selectedOperaionRun?.runId}
        />
        <SCBOneAlertCard
          serial={device.serial}
          selectedFromToFilter={alertFromToFilter}
          operationRunSelected={!!selectedOperaionRun?.runId}
        />
        <SCBOneOperationRunsCard
          serial={device.serial}
          positions={operationRunPositions.value?.items || []}
          positionsLoading={operationRunPositions.loading}
          selectedRow={selectedOperaionRun?.runId}
          onRowSelected={onRowSelected}
        />
        <SCBOneOperationRunPositionCard
          operationRun={selectedOperaionRun}
          allOperationRunPositions={operationRunPositions.value?.items || []}
          allOperationRunPositionsLoading={operationRunPositions.loading}
        />
        <SCBOneLocationCard
          positions={operationRunPositions.value?.items}
          operationRunSelected={!!selectedOperaionRun?.runId}
          isLoading={operationRunPositions.loading}
        />
      </div>
    </div>
  );
}
